/**
 * InterviewHub — list of interviews for a client.
 * Linked from ClientEditorHub as a standalone activity.
 * Route: /coordinator/clients/:clientId/interviews
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";
import { landingFor } from "../../lib/roles.js";
import BackLink from "../../components/BackLink.jsx";

const STATUS_LABEL = {
  draft:    { text: "Draft",     color: "var(--color-text-tertiary)" },
  invited:  { text: "Sent",      color: "var(--color-text-tertiary)" },
  active:   { text: "In progress", color: "#d97706" },
  complete: { text: "Complete",  color: "var(--color-accent)" },
  partial:  { text: "Partial",   color: "#d97706" },
  fed:      { text: "Fed to extraction", color: "var(--color-status-live)" },
};

export default function InterviewHub() {
  const { clientId } = useParams();
  const navigate     = useNavigate();
  const { user, can } = useAuth();
  // Builders set up interviews for anyone and feed finished ones to review. A
  // Contributor (Oct 24) sees only her own interviews and may start one for
  // herself — the route admits her and the setup page drops "who is answering".
  // Per client once the client loads (viewer_capabilities); account-level until then.
  const [caps,         setCaps]         = useState(null);
  const canBuild = caps ? caps.includes("edit_client") : can("build_profiles");
  const [interviews,   setInterviews]   = useState(null);
  const [clientName,   setClientName]   = useState("");
  const [creating,     setCreating]     = useState(false);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`/api/clients/${clientId}/interviews`).then(r => r.json()),
      fetch(`/api/clients/${clientId}`).then(r => r.json()),
    ]).then(([ivs, cl]) => {
      setInterviews(Array.isArray(ivs) ? ivs : []);
      setClientName(cl.display_name || "");
      setCaps(cl.viewer_capabilities || null);
    }).catch(e => setError(e.message));
  }, [clientId]);

  async function handleNew() {
    // Navigate to setup — let the setup page create the interview
    navigate(`/coordinator/clients/${clientId}/interviews/new`);
  }

  const editorHub = `/coordinator/clients/${clientId}/edit`;

  return (
    <div style={s.page}>
      <div style={s.content}>
        <ClientBreadcrumb clientName={clientName} role={user?.role} clientId={clientId} />
        <div style={s.headerRow}>
          <div>
            <h1 style={s.title}>Interviews</h1>
            <p style={s.subtitle}>
              {canBuild
                ? "Conduct a structured interview with support staff or family member to gather insights that supplement the ISP."
                : `Answer questions about ${clientName || "them"} in your own words. Everything you add helps the people caring for ${clientName || "them"} know them better.`}
            </p>
          </div>
          {/* A contributor may start one for herself (Oct 24); the setup page
              knows the difference. */}
          <button style={s.newBtn} onClick={handleNew} disabled={creating}>
            {canBuild ? "+ New interview" : "+ Create a new interview"}
          </button>
        </div>

        {canBuild
          ? <BackLink to={editorHub} />
          : <BackLink to={landingFor(user)}>← Back to profiles</BackLink>}

        {error && <p style={s.error}>{error}</p>}
        {interviews === null && <p style={s.hint}>Loading…</p>}
        {interviews?.length === 0 && (
          <div style={s.empty}>
            <p style={{ fontSize: 40 }}>🎙</p>
            <p style={s.emptyTitle}>
              {canBuild ? "No interviews yet" : "Nothing to answer just yet"}
            </p>
            {/* An honest empty state, in each audience's words. */}
            <p style={s.hint}>
              {canBuild
                ? "Create an interview to gather structured insights from support staff and family members."
                : "When someone sends you an interview, it will appear here. You can also start one yourself."}
            </p>
          </div>
        )}

        {interviews && interviews.length > 0 && (() => {
          // Triage: group interviews by what needs the coordinator's attention.
          const groups = { attention: [], inProgress: [], notStarted: [], done: [] };
          for (const iv of interviews) {
            const objs = iv.objectives || [];
            const allDone = objs.length > 0 && objs.every(o => o.interaction?.status === "complete");
            if (iv.status === "complete" || (iv.status === "active" && allDone)) {
              groups.attention.push(iv);
            } else if (iv.status === "active") {
              groups.inProgress.push(iv);
            } else if (iv.status === "draft" || iv.status === "invited") {
              groups.notStarted.push(iv);
            } else {
              groups.done.push(iv);   // fed
            }
          }
          // A contributor sees only her own interviews (Oct 24), in her words.
          const sections = canBuild ? [
            { key: "attention",  title: "Needs your attention", hint: "Finished — ready to review and feed" },
            { key: "inProgress", title: "In progress",          hint: "Pick up where you left off" },
            { key: "notStarted", title: "Not started",          hint: null },
            { key: "done",       title: "Done",                 hint: "Fed to extraction" },
          ] : [
            { key: "inProgress", title: "In progress",          hint: "Pick up where you left off" },
            { key: "notStarted", title: "Ready for you",        hint: null },
            { key: "attention",  title: "Finished",             hint: "Thank you" },
          ];
          if (!canBuild) { groups.attention.push(...groups.done); groups.done = []; }
          return (
            <div style={s.groups}>
              {sections.map(sec => groups[sec.key].length > 0 && (
                <div key={sec.key} style={s.group}>
                  <div style={s.groupHead}>
                    <span style={s.groupTitle}>{sec.title}</span>
                    <span style={s.groupCount}>{groups[sec.key].length}</span>
                    {sec.hint && <span style={s.groupHint}>{sec.hint}</span>}
                  </div>
                  <div style={s.list}>
                    {groups[sec.key].map(iv => (
                      <InterviewCard key={iv.id} iv={iv} clientId={clientId} canBuild={canBuild} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function InterviewCard({ iv, clientId, canBuild }) {
  const st = (!canBuild && iv.status === "fed")
    ? { text: "Added", color: STATUS_LABEL.fed.color }
    : (STATUS_LABEL[iv.status] || STATUS_LABEL.draft);
  const objCount = iv.objectives?.length || 0;
  const doneCount = iv.objectives?.filter(o => o.interaction?.status === "complete").length || 0;
  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <div>
          <p style={s.respondentName}>
            {iv.respondent?.display_name || iv.respondent?.role || "Unknown respondent"}
            <span style={s.roleChip}>{iv.respondent?.role}</span>
          </p>
          <p style={s.cardMeta}>
            {objCount} objective{objCount !== 1 ? "s" : ""}
            {objCount > 0 && ` · ${doneCount} of ${objCount} complete`}
          </p>
        </div>
        <span style={{ ...s.statusBadge, color: st.color }}>{st.text}</span>
      </div>
      <div style={s.cardActions}>
        {iv.status !== "fed" && (
          <Link to={`/coordinator/clients/${clientId}/interviews/${iv.id}/session`}
            style={s.actionBtn}>
            {iv.status === "draft" || iv.status === "invited" ? "Start →"
              : iv.status === "complete" ? (canBuild ? "Review / Feed →" : "See your answers →")
              : "Continue →"}
          </Link>
        )}
        {iv.status === "fed" && !canBuild && (
          <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" }}>
            Added to the profile. Thank you.
          </span>
        )}
        {iv.status === "fed" && canBuild && (
          <Link to={`/coordinator/clients/${clientId}/review`}
            style={{ fontSize: "var(--text-xs)", color: "var(--color-accent)", textDecoration: "none" }}>
            Fed to extraction — review pending content →
          </Link>
        )}
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: "100dvh", background: "var(--color-bg-subtle)" },
  content:     { maxWidth: 720, margin: "0 auto", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-5)" },
  headerRow:   { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-4)", flexWrap: "wrap" },
  title:       { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-bold)", color: "var(--color-text-primary)" },
  subtitle:    { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", maxWidth: 480 },
  newBtn:      { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  empty:       { display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-12)", textAlign: "center" },
  emptyTitle:  { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  hint:        { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  error:       { fontSize: "var(--text-sm)", color: "var(--color-critical)" },
  list:        { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  groups:      { display: "flex", flexDirection: "column", gap: "var(--space-6)" },
  group:       { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  groupHead:   { display: "flex", alignItems: "baseline", gap: "var(--space-2)" },
  groupTitle:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", textTransform: "uppercase", letterSpacing: "0.06em" },
  groupCount:  { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "#fff", background: "var(--color-text-tertiary)", borderRadius: "var(--radius-full)", padding: "1px 7px" },
  groupHint:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginLeft: "var(--space-1)" },
  card:        { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  cardTop:     { display: "flex", alignItems: "flex-start", justifyContent: "space-between" },
  respondentName: { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: "var(--space-2)" },
  roleChip:    { fontSize: 10, background: "var(--color-bg-inset)", color: "var(--color-text-tertiary)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.05em" },
  cardMeta:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: "var(--space-1)" },
  statusBadge: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)" },
  cardActions: { display: "flex", gap: "var(--space-3)", alignItems: "center" },
  actionBtn:   { fontSize: "var(--text-sm)", color: "var(--color-accent)", textDecoration: "none", fontWeight: "var(--weight-medium)" },
};
